// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import type { RouteDefinition } from '../../../core/utils/endpoints.ts'
import {
  EmailCampaignGridModel,
  EmailSenderOption,
  EmailDelegationsModel,
  EmailMySignatureModel,
  EmailResolveSignatureModel,
  validateEmailCampaignGridModel,
  validateEmailSenderOption,
  validateEmailDelegationsModel,
  validateEmailMySignatureModel,
  validateEmailResolveSignatureModel
} from '../../../state_manager/data_models/email_campaign_grid_model.ts'
import {
  EmailRecipientGridModel,
  validateEmailRecipientGridModel,
  EmailRecipientStagePayload,
  EmailRecipientOverridePayload
} from '../../../state_manager/data_models/email_recipient_grid_model.ts'
import {
  DistributionListRecipientSchema,
  type DistributionListRecipient
} from '../../../state_manager/data_models/distribution_list_recipients_model.ts'
import {
  validateEmailCampaignAccountFiles,
  type EmailCampaignAccountFiles
} from '../../../state_manager/data_models/email_campaign_account_files_model.ts'
import {
  validateEmailChannels,
  type EmailChannels
} from '../../../state_manager/data_models/email_channels_model.ts'
import {
  validateEmailCampaignCopies,
  type EmailCampaignCopies,
  type EmailCampaignCopyKind
} from '../../../state_manager/data_models/email_campaign_copy_model.ts'
import {
  FilterValue,
  SortConfig,
  EmailCampaignsPaginatedResponse,
  EmailCampaignSavePayload,
  EmailSaveSignaturePayload,
  EmailGrantDelegationPayload,
  EmailResolveSignaturePayload,
  EmailResolveRecipientsPayload
} from '../../../state_manager/interfaces/email.ts'
import { readJsonBody } from '../../../core/utils/utilities.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export type { EmailCampaignsPaginatedResponse }

// ── Email campaigns: list / filter / detail / save / delete ────────────────

export const fetchEmailCampaignsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<EmailCampaignsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_FILTER), {
      method: API_ROUTES.EMAIL.CAMPAIGNS_FILTER.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch email campaigns: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rawRows = Array.isArray(json.data) ? json.data : []

    const validated: EmailCampaignGridModel[] = []
    for (const row of rawRows) {
      try {
        validated.push(validateEmailCampaignGridModel(row))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Email campaign row failed validation:', e, row)
        }
      }
    }

    return {
      data: validated,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : validated.length
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching email campaigns:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching email campaigns'
    }
  }
}

// Phase 5: multiselect option universes for the Email Log audit grid.
// Keyed by the same filter_keys the data RPC's filter handler matches.
export interface FilterOption {
  value: string
  label: string
}

export interface EmailCampaignsFilterOptions {
  email_campaigns__status: FilterOption[]
  email_campaigns__email_template_id: FilterOption[]
  email_campaigns__sent_as_user_id: FilterOption[]
  email_campaigns__created_by: FilterOption[]
}

const EMPTY_FILTER_OPTIONS: EmailCampaignsFilterOptions = {
  email_campaigns__status: [],
  email_campaigns__email_template_id: [],
  email_campaigns__sent_as_user_id: [],
  email_campaigns__created_by: []
}

export interface EmailCampaignsFilterOptionsResponse {
  data: EmailCampaignsFilterOptions
  error?: string
}

export const fetchEmailCampaignsFilterOptions = async (
  accessToken: string | null = null
): Promise<EmailCampaignsFilterOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_FILTER_OPTIONS),
      { method: API_ROUTES.EMAIL.CAMPAIGNS_FILTER_OPTIONS.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch filter options: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: Partial<EmailCampaignsFilterOptions> }
    const raw = (json.data && typeof json.data === 'object') ? json.data : {}
    const safe: EmailCampaignsFilterOptions = {
      email_campaigns__status:            Array.isArray(raw.email_campaigns__status)            ? raw.email_campaigns__status            : [],
      email_campaigns__email_template_id: Array.isArray(raw.email_campaigns__email_template_id) ? raw.email_campaigns__email_template_id : [],
      email_campaigns__sent_as_user_id:   Array.isArray(raw.email_campaigns__sent_as_user_id)   ? raw.email_campaigns__sent_as_user_id   : [],
      email_campaigns__created_by:        Array.isArray(raw.email_campaigns__created_by)        ? raw.email_campaigns__created_by        : []
    }
    return { data: safe }
  } catch (error) {
    return {
      data: EMPTY_FILTER_OPTIONS,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export interface EmailCampaignDetailResponse {
  data: EmailCampaignGridModel | null
  error?: string
}

export const fetchEmailCampaignDetail = async (
  campaignId: string,
  accessToken: string | null = null
): Promise<EmailCampaignDetailResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_DETAIL, { campaignId }),
      { method: API_ROUTES.EMAIL.CAMPAIGNS_DETAIL.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch email campaign: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }
    return { data: validateEmailCampaignGridModel(json.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching email campaign detail:', error)
    }
    return {
      data: null,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching the email campaign'
    }
  }
}

export interface EmailCampaignWriteResponse {
  data: { email_campaigns__id: string } | null
  error?: string
}

const writeEmailCampaign = async (
  url: string,
  method: string,
  payload: EmailCampaignSavePayload,
  accessToken: string | null
): Promise<EmailCampaignWriteResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(url, {
      method,
      headers,
      body: JSON.stringify({ data: payload })
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Request failed: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: { email_campaigns__id: string } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const createEmailCampaign = async (
  payload: EmailCampaignSavePayload,
  accessToken: string | null = null
): Promise<EmailCampaignWriteResponse> =>
  writeEmailCampaign(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_CREATE),
    API_ROUTES.EMAIL.CAMPAIGNS_CREATE.method,
    payload,
    accessToken
  )

export const updateEmailCampaign = async (
  campaignId: string,
  payload: EmailCampaignSavePayload,
  accessToken: string | null = null
): Promise<EmailCampaignWriteResponse> =>
  writeEmailCampaign(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_UPDATE, { campaignId }),
    API_ROUTES.EMAIL.CAMPAIGNS_UPDATE.method,
    payload,
    accessToken
  )

export const deleteEmailCampaign = async (
  campaignId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_DELETE, { campaignId }),
      { method: API_ROUTES.EMAIL.CAMPAIGNS_DELETE.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to delete email campaign: ${response.status} ${response.statusText}`
      )
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Sender pickers ─────────────────────────────────────────────────────────

export interface EmailSenderOptionsResponse {
  data: EmailSenderOption[]
  error?: string
}

const validateSenderArray = (raw: unknown[]): EmailSenderOption[] => {
  const validated: EmailSenderOption[] = []
  for (const row of raw) {
    try {
      validated.push(validateEmailSenderOption(row))
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Sender option failed validation:', e, row)
      }
    }
  }
  return validated
}

export const fetchEligibleSenders = async (
  accessToken: string | null = null
): Promise<EmailSenderOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.ME_ELIGIBLE_SENDERS),
      { method: API_ROUTES.EMAIL.ME_ELIGIBLE_SENDERS.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch eligible senders: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[] }
    const rawRows = Array.isArray(json.data) ? json.data : []
    return { data: validateSenderArray(rawRows) }
  } catch (error) {
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const searchSystemUsers = async (
  searchTerm: string,
  accessToken: string | null = null
): Promise<EmailSenderOptionsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.SYSTEM_USERS_SEARCH),
      {
        method: API_ROUTES.EMAIL.SYSTEM_USERS_SEARCH.method,
        headers,
        body: JSON.stringify({ data: { search_term: searchTerm } })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to search system users: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[] }
    const rawRows = Array.isArray(json.data) ? json.data : []
    return { data: validateSenderArray(rawRows) }
  } catch (error) {
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── My signature ───────────────────────────────────────────────────────────

export interface EmailMySignatureResponse {
  data: EmailMySignatureModel | null
  error?: string
}

export const fetchMySignature = async (
  accessToken: string | null = null
): Promise<EmailMySignatureResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.ME_SIGNATURE_GET),
      { method: API_ROUTES.EMAIL.ME_SIGNATURE_GET.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch signature: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }
    return { data: validateEmailMySignatureModel(json.data) }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const saveMySignature = async (
  payload: EmailSaveSignaturePayload,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.ME_SIGNATURE_SAVE),
      {
        method: API_ROUTES.EMAIL.ME_SIGNATURE_SAVE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to save signature: ${response.status} ${response.statusText}`
      )
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// Resolve signature HTML for a given sent-as user (Send-as picker change)
export interface EmailResolveSignatureResponse {
  data: EmailResolveSignatureModel | null
  error?: string
}

export const resolveSenderSignature = async (
  payload: EmailResolveSignaturePayload,
  accessToken: string | null = null
): Promise<EmailResolveSignatureResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.SIGNATURE_RESOLVE),
      {
        method: API_ROUTES.EMAIL.SIGNATURE_RESOLVE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to resolve signature: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }
    return { data: validateEmailResolveSignatureModel(json.data) }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Delegations ────────────────────────────────────────────────────────────

export interface EmailDelegationsResponse {
  data: EmailDelegationsModel | null
  error?: string
}

export const fetchMyDelegations = async (
  accessToken: string | null = null
): Promise<EmailDelegationsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.DELEGATIONS_LIST),
      { method: API_ROUTES.EMAIL.DELEGATIONS_LIST.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch delegations: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown }
    if (json.data == null) return { data: null }
    return { data: validateEmailDelegationsModel(json.data) }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const grantDelegation = async (
  payload: EmailGrantDelegationPayload,
  accessToken: string | null = null
): Promise<{ data: { email_sender_delegations__id: string } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.DELEGATIONS_GRANT),
      {
        method: API_ROUTES.EMAIL.DELEGATIONS_GRANT.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to grant delegation: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as {
      data?: { email_sender_delegations__id: string } | null
    }
    return { data: json.data ?? null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Phase 3 — Recipients (resolve, list, stage, override) ──────────────────

export interface EmailResolveRecipientsResponse {
  data: DistributionListRecipient[]
  error?: string
}

// Resolve a (possibly amended) distribution-list definition to its recipient
// set for an email campaign. Backend reuses the distribution-lists resolver;
// the response is a flat array of recipient rows. We strict-validate each
// row against the shared Zod schema (Phase 2 gotcha #1: bare .optional()
// Zod rejects JSON null — the recipient model uses .nullable().optional()).
export const resolveEmailRecipients = async (
  campaignId: string,
  payload: EmailResolveRecipientsPayload,
  accessToken: string | null = null
): Promise<EmailResolveRecipientsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.RECIPIENTS_RESOLVE, { campaignId }),
      {
        method: API_ROUTES.EMAIL.RECIPIENTS_RESOLVE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to resolve email recipients: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: unknown[] }
    const rawRows = Array.isArray(json.data) ? json.data : []

    const validated: DistributionListRecipient[] = []
    for (const row of rawRows) {
      const parsed = DistributionListRecipientSchema.safeParse(row)
      if (parsed.success) validated.push(parsed.data)
      else if (process.env.NODE_ENV === 'development') {
        console.warn('Email resolve row failed validation:', parsed.error, row)
      }
    }
    return { data: validated }
  } catch (error) {
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export interface EmailRecipientsPaginatedResponse {
  data: EmailRecipientGridModel[]
  hasMore: boolean
  totalCount: number
  error?: string
}

// Filtered/sorted/paginated grid of staged recipients under a campaign.
// Same envelope as the campaigns list: {data, has_more, count}.
export const fetchEmailRecipientsFiltered = async (
  campaignId: string,
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null
): Promise<EmailRecipientsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const requestBody = {
      data: {
        page,
        page_size: pageSize ?? 200,
        sorting: sorting.map((s) => ({ key: s.key, direction: s.direction })),
        filters: filters.map((f) => ({ key: f.key, value: f.value }))
      }
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.RECIPIENTS_FILTER, { campaignId }),
      {
        method: API_ROUTES.EMAIL.RECIPIENTS_FILTER.method,
        headers,
        body: JSON.stringify(requestBody)
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch email recipients: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[]; has_more?: boolean; count?: number }
    const rawRows = Array.isArray(json.data) ? json.data : []

    const validated: EmailRecipientGridModel[] = []
    for (const row of rawRows) {
      try {
        validated.push(validateEmailRecipientGridModel(row))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Email recipient row failed validation:', e, row)
        }
      }
    }

    return {
      data: validated,
      hasMore: Boolean(json.has_more),
      totalCount: typeof json.count === 'number' ? json.count : validated.length
    }
  } catch (error) {
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error:
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while fetching email recipients'
    }
  }
}

// Replace the staged recipient set under a draft campaign. Returns the
// recipient_count + campaign id so the caller can refresh its grid.
export interface EmailStageRecipientsResponse {
  data: { email_campaigns__id: string; recipient_count: number } | null
  error?: string
}

export const stageEmailRecipients = async (
  campaignId: string,
  payload: EmailRecipientStagePayload,
  accessToken: string | null = null
): Promise<EmailStageRecipientsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.RECIPIENTS_STAGE, { campaignId }),
      {
        method: API_ROUTES.EMAIL.RECIPIENTS_STAGE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to stage recipients: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as {
      data?: { email_campaigns__id: string; recipient_count: number } | null
    }
    return { data: json.data ?? null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// Per-recipient override (subject + body + role). Body is sanitized
// server-side at the single choke point (same pattern as save_email_campaign).
export const saveEmailRecipientOverride = async (
  recipientId: string,
  payload: EmailRecipientOverridePayload,
  accessToken: string | null = null
): Promise<{ data: { email_recipients__id: string } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.RECIPIENT_OVERRIDE_SAVE, { recipientId }),
      {
        method: API_ROUTES.EMAIL.RECIPIENT_OVERRIDE_SAVE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to save override: ${response.status} ${response.statusText}`
      )
    }

    const json = (await response.json()) as { data?: { email_recipients__id: string } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Phase 4 — Queue + dispatch retry ───────────────────────────────────────

export interface EmailQueueCampaignResponse {
  data: {
    email_campaigns__id: string
    recipient_count: number
    sent_as_user_id: string
    sent_as_email: string
    provider_key: string
  } | null
  error?: string
  // Forwarded so the caller can show different UI for 403 (delegation) vs
  // 400 (validation) vs 500 (anything else).
  status?: number
}

// Queue a draft campaign for dispatch. Backend validates delegation +
// send-mode + sending-domain + recipient count + size, flips state to
// 'queued', then fire-and-forgets the dispatch edge invoke.
export const queueEmailCampaign = async (
  campaignId: string,
  accessToken: string | null = null
): Promise<EmailQueueCampaignResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_QUEUE, { campaignId }),
      {
        method: API_ROUTES.EMAIL.CAMPAIGNS_QUEUE.method,
        headers,
        body: JSON.stringify({ data: {} })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      return {
        data: null,
        status: response.status,
        error: body?.message || `Failed to queue campaign: ${response.status} ${response.statusText}`
      }
    }

    const json = (await response.json()) as {
      data?: EmailQueueCampaignResponse['data']
    }
    return { data: json.data ?? null, status: response.status }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// Cancel a queued campaign before the dispatch flips it to sending.
// Backend RPC refuses 'sending' (already-dispatching race) and treats
// 'cancelled' as an idempotent no-op success. 400 on non-queued status,
// 500 on anything else.
export interface EmailCancelCampaignResponse {
  data: {
    email_campaigns__id: string
    cancelled_recipients: number
    status_before: string
  } | null
  error?: string
  status?: number
}

export const cancelEmailCampaign = async (
  campaignId: string,
  accessToken: string | null = null
): Promise<EmailCancelCampaignResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_CANCEL, { campaignId }),
      {
        method: API_ROUTES.EMAIL.CAMPAIGNS_CANCEL.method,
        headers,
        body: JSON.stringify({ data: {} })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      return {
        data: null,
        status: response.status,
        error: body?.message || `Failed to cancel campaign: ${response.status} ${response.statusText}`
      }
    }

    const json = (await response.json()) as {
      data?: EmailCancelCampaignResponse['data']
    }
    return { data: json.data ?? null, status: response.status }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// Halt / resume a sending campaign (2026-09-15 dispatch stall warning).
// Halt: 'sending' → 'halted'; the dispatch stops at its next batch flush and
// returns unsent recipients to 'queued'. Resume: 'halted' → 'queued' + a
// dispatch invoke; refused (400) while recipients are still unconfirmed.
export interface EmailHaltCampaignResponse {
  data: {
    email_campaigns__id: string
    status_before: string
    pending_count: number
  } | null
  error?: string
  status?: number
}

export interface EmailResumeCampaignResponse {
  data: {
    email_campaigns__id: string
    resumed: boolean
    queued_count: number
    status: string
  } | null
  error?: string
  status?: number
}

// POST a body-less campaign state action ({data:{}} envelope) and surface the
// backend's message on failure. Shared by halt, resume and reconcile.
const postCampaignAction = async <T>(
  route: RouteDefinition,
  campaignId: string,
  verb: string,
  accessToken: string | null
): Promise<{ data: T | null; error?: string; status?: number }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route, { campaignId }), {
      method: route.method,
      headers,
      body: JSON.stringify({ data: {} })
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      return {
        data: null,
        status: response.status,
        error: body?.message || `Failed to ${verb} campaign: ${response.status} ${response.statusText}`
      }
    }

    const json = (await response.json()) as { data?: T }
    return { data: json.data ?? null, status: response.status }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export interface EmailReconcileCampaignResponse {
  data: {
    email_campaigns__id: string
    candidates: number
    confirmed_sent: number
    requeued: number
    settling: number
    // What the recipients were checked against: Postmark's message history,
    // or - for a campaign sent from a mailbox - the sender's own mailbox.
    checked: 'postmark' | 'sent_items'
    messages_scanned: number
    postmark_messages_scanned: number
  } | null
  error?: string
  status?: number
}

// "Check Postmark & re-queue" / "Check Sent Items & re-queue" — synchronous;
// can take a few seconds while the edge function pages through Postmark's
// message search, or the sender's mailbox for a mailbox campaign.
export const reconcileEmailCampaign = (
  campaignId: string,
  accessToken: string | null = null
): Promise<EmailReconcileCampaignResponse> =>
  postCampaignAction<NonNullable<EmailReconcileCampaignResponse['data']>>(
    API_ROUTES.EMAIL.CAMPAIGNS_RECONCILE, campaignId, 'check', accessToken
  )

export interface EmailRequeueFailedResponse {
  data: {
    email_campaigns__id: string
    requeued: number
  } | null
  error?: string
  status?: number
}

// "Re-queue failed" on a failed / partially failed MAILBOX campaign: the
// recipients that never left the mailbox go back to the queue. Sends nothing;
// the caller follows with retryEmailDispatch.
export const requeueFailedEmailRecipients = (
  campaignId: string,
  accessToken: string | null = null
): Promise<EmailRequeueFailedResponse> =>
  postCampaignAction<NonNullable<EmailRequeueFailedResponse['data']>>(
    API_ROUTES.EMAIL.CAMPAIGNS_REQUEUE_FAILED, campaignId, 're-queue the failed recipients of the', accessToken
  )

export const haltEmailCampaign = (
  campaignId: string,
  accessToken: string | null = null
): Promise<EmailHaltCampaignResponse> =>
  postCampaignAction<NonNullable<EmailHaltCampaignResponse['data']>>(
    API_ROUTES.EMAIL.CAMPAIGNS_HALT, campaignId, 'halt', accessToken
  )

export const resumeEmailCampaign = (
  campaignId: string,
  accessToken: string | null = null
): Promise<EmailResumeCampaignResponse> =>
  postCampaignAction<NonNullable<EmailResumeCampaignResponse['data']>>(
    API_ROUTES.EMAIL.CAMPAIGNS_RESUME, campaignId, 'resume', accessToken
  )

// Copy a design template's attachments onto a draft campaign. The backend
// duplicates Storage objects into the campaign's folder and inserts
// file_urls_email_campaigns rows; files whose name is already attached
// are skipped (re-applying a template is a no-op, not a duplicator).
export interface EmailApplyTemplateAttachmentsResponse {
  data: {
    copied: Array<{ id: string; file_name: string; file_url: string; type: string }>
    skipped: Array<{ file_name: string; reason: string }>
    copied_count: number
    skipped_count: number
  } | null
  error?: string
}

export const applyTemplateAttachments = async (
  campaignId: string,
  emailDesignTemplateId: string,
  accessToken: string | null = null
): Promise<EmailApplyTemplateAttachmentsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_APPLY_TEMPLATE_ATTACHMENTS, {
        campaignId
      }),
      {
        method: API_ROUTES.EMAIL.CAMPAIGNS_APPLY_TEMPLATE_ATTACHMENTS.method,
        headers,
        body: JSON.stringify({ data: { email_design_template_id: emailDesignTemplateId } })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      return {
        data: null,
        error:
          body?.message ||
          `Failed to load template attachments: ${response.status} ${response.statusText}`
      }
    }

    const json = (await response.json()) as {
      data?: EmailApplyTemplateAttachmentsResponse['data']
    }
    return { data: json.data ?? null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// Re-invoke the dispatch edge function for an already-queued campaign.
// Useful when the first fire-and-forget invoke didn't land (cold start,
// network blip). Server-side idempotency means already-sent recipients
// are skipped on a re-run.
export const retryEmailDispatch = async (
  campaignId: string,
  accessToken: string | null = null
): Promise<{ data: { email_campaigns__id: string; status_at_retry: string } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_DISPATCH_RETRY, { campaignId }),
      {
        method: API_ROUTES.EMAIL.CAMPAIGNS_DISPATCH_RETRY.method,
        headers,
        body: JSON.stringify({ data: {} })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to retry dispatch: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as {
      data?: { email_campaigns__id: string; status_at_retry: string } | null
    }
    return { data: json.data ?? null }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Track D1 — Test send (compose form's Test Send modal) ─────────────────

export interface EmailTestAccount {
  id: string
  name: string
  label: string
}

export interface EmailTestContact {
  contact_id: string
  account_id: string
  first_name: string
  last_name: string
  email: string
  is_self: boolean
}

export interface EmailTestRecipientsResponse {
  data: {
    account: EmailTestAccount | null
    contacts: EmailTestContact[]
  }
  error?: string
}

// Fetch the cfg-pointed test account + its contacts for the compose
// form's Test Send modal. The modal shows the account name as a header
// and the contacts as a checkbox list (is_self pre-checked). On top of
// that the operator can type ad-hoc addresses not tied to the account.
// Account is null when the cfg row is missing OR points at an account
// that no longer resolves — the modal degrades to ad-hoc-only.
export const fetchEmailTestRecipients = async (
  accessToken: string | null = null
): Promise<EmailTestRecipientsResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.TEST_RECIPIENTS_GET),
      { method: API_ROUTES.EMAIL.TEST_RECIPIENTS_GET.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to fetch test recipients: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as {
      data?: { account?: EmailTestAccount | null; contacts?: unknown[] }
    }
    const rawAccount = json.data?.account ?? null
    const account: EmailTestAccount | null = rawAccount && rawAccount.id
      ? {
          id:    String(rawAccount.id),
          name:  String(rawAccount.name ?? ''),
          label: String(rawAccount.label ?? rawAccount.name ?? '')
        }
      : null

    const rawContacts = Array.isArray(json.data?.contacts) ? (json.data!.contacts as unknown[]) : []
    const contacts: EmailTestContact[] = []
    for (const item of rawContacts) {
      if (!item || typeof item !== 'object') continue
      const o = item as Record<string, unknown>
      const cid = typeof o.contact_id === 'string' ? o.contact_id : ''
      const aid = typeof o.account_id === 'string' ? o.account_id : ''
      const email = typeof o.email === 'string' ? o.email.trim().toLowerCase() : ''
      if (!cid || !email) continue
      contacts.push({
        contact_id: cid,
        account_id: aid,
        first_name: typeof o.first_name === 'string' ? o.first_name : '',
        last_name:  typeof o.last_name === 'string' ? o.last_name : '',
        email,
        is_self:    o.is_self === true
      })
    }

    return { data: { account, contacts } }
  } catch (error) {
    return {
      data: { account: null, contacts: [] },
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// Wire shape for a single test-send recipient. Contact-linked entries
// carry contact_id + account_id; ad-hoc entries carry only the email.
// Backend dedupes by lowercased email and builds real merge_data for
// contact-linked entries (same path stage_email_recipients uses).
export interface EmailTestSendRecipientPayload {
  email: string
  contact_id?: string
  account_id?: string
}

export interface EmailTestSendResponse {
  data: {
    email_campaigns__id: string
    sent_count: number
    failed_count: number
    errors: Array<{ email: string; message: string }>
    provider_key: string | null
    recipient_emails: string[]
    sent_as_email?: string | null
  } | null
  error?: string
  status?: number
}

// Test-send the compose draft to a chosen set of (contact-linked or
// ad-hoc) recipients. Synchronous — the modal awaits the result so it
// can show sent_count / per-recipient errors inline. Backend validates
// draft status, delegation, sending domain, email syntax, and
// per-message cap; surface verbatim errors.
// asAccountId (optional): send the test AS one staged account - every test
// recipient gets that account's merge values and, on the mailbox channel,
// its own attachments.
export const testSendEmailCampaign = async (
  campaignId: string,
  recipients: EmailTestSendRecipientPayload[],
  accessToken: string | null = null,
  asAccountId: string | null = null
): Promise<EmailTestSendResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_TEST_SEND, { campaignId }),
      {
        method: API_ROUTES.EMAIL.CAMPAIGNS_TEST_SEND.method,
        headers,
        body: JSON.stringify({
          data: asAccountId ? { recipients, as_account_id: asAccountId } : { recipients }
        })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string; data?: EmailTestSendResponse['data'] }>(response)
      return {
        data: body?.data ?? null,
        status: response.status,
        error: body?.message || `Failed to test send: ${response.status} ${response.statusText}`
      }
    }

    const json = (await response.json()) as { data?: EmailTestSendResponse['data'] }
    return { data: json.data ?? null, status: response.status }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Phase 4b — Team-mode UX ────────────────────────────────────────────────

export interface TeamCandidateContact {
  value: string
  label: string
  email: string
  first_name: string
  last_name: string
}

export const saveTeamOverride = async (
  campaignId: string,
  accountId: string,
  payload: { subject_override?: string | null; body_override_html?: string | null },
  accessToken: string | null = null
): Promise<{ data: { email_campaigns__id: string; accounts__id: string; rows_updated: number } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.TEAM_OVERRIDE_SAVE, { campaignId, accountId }),
      {
        method: API_ROUTES.EMAIL.TEAM_OVERRIDE_SAVE.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to save team override: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: { email_campaigns__id: string; accounts__id: string; rows_updated: number } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const addTeamRecipient = async (
  campaignId: string,
  accountId: string,
  payload: { contact_id: string; role: 'to' | 'cc' },
  accessToken: string | null = null
): Promise<{ data: { email_recipients__id: string; recipient_count: number } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.TEAM_RECIPIENT_ADD, { campaignId, accountId }),
      {
        method: API_ROUTES.EMAIL.TEAM_RECIPIENT_ADD.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to add team recipient: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: { email_recipients__id: string; recipient_count: number } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const searchTeamCandidates = async (
  campaignId: string,
  accountId: string,
  searchTerm: string | null,
  accessToken: string | null = null
): Promise<{ data: TeamCandidateContact[]; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.TEAM_CANDIDATE_SEARCH, { campaignId, accountId }),
      {
        method: API_ROUTES.EMAIL.TEAM_CANDIDATE_SEARCH.method,
        headers,
        body: JSON.stringify({ data: { search_term: searchTerm } })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to search team candidates: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: TeamCandidateContact[] }
    return { data: Array.isArray(json.data) ? json.data : [] }
  } catch (error) {
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const removeRecipient = async (
  recipientId: string,
  accessToken: string | null = null
): Promise<{ data: { email_recipients__id: string; recipient_count: number } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.RECIPIENT_REMOVE, { recipientId }),
      {
        method: API_ROUTES.EMAIL.RECIPIENT_REMOVE.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to remove recipient: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: { email_recipients__id: string; recipient_count: number } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}


export const revokeDelegation = async (
  delegationId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.DELEGATIONS_REVOKE, { delegationId }),
      { method: API_ROUTES.EMAIL.DELEGATIONS_REVOKE.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message || `Failed to revoke delegation: ${response.status} ${response.statusText}`
      )
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ── Campaign-level Cc / Bcc copy recipients ────────────────────────────────
//
// Add/remove persist immediately to the backend (draft-only, enforced by the
// RPC) — mirrors the team-recipient mutators above. The list response is
// strict-validated with the Zod schema (CLAUDE.md: a response that fails
// validation is an error, not coerced). Contact selection reuses the global
// /contacts/search typeahead (returns the email so the picker can render the
// two-line option mandated by the distribution-lists §2.15f ruling).

export const fetchCampaignCopies = async (
  campaignId: string,
  accessToken: string | null = null
): Promise<{ data: EmailCampaignCopies; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.COPIES_LIST, { campaignId }),
      { method: API_ROUTES.EMAIL.COPIES_LIST.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to load Cc/Bcc: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown }
    const validated = validateEmailCampaignCopies(json.data ?? { cc: [], bcc: [] })
    return { data: validated }
  } catch (error) {
    return {
      data: { cc: [], bcc: [] },
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ─── Per-account attachments (capital call notices, stage 5) ────────────────
//
// The files of a campaign that belong to one account each, and the summary
// the queue gate checks. Read-only: nothing on the compose form writes them.
// data is null when the read failed - the caller shows no per-account
// furniture rather than a wrong "no files".

export const fetchEmailCampaignAccountFiles = async (
  campaignId: string,
  accessToken: string | null = null
): Promise<{ data: EmailCampaignAccountFiles | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CAMPAIGNS_ACCOUNT_FILES, { campaignId }),
      { method: API_ROUTES.EMAIL.CAMPAIGNS_ACCOUNT_FILES.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(
        body?.message ||
          `Failed to load the per-account attachments: ${response.status} ${response.statusText}`
      )
    }
    const json = (await response.json()) as { data?: unknown }
    if (!json.data) throw new Error('The per-account attachments came back empty')
    return { data: validateEmailCampaignAccountFiles(json.data) }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

// ─── Send type options + the mailbox channel (capital call notices, stage 6a) ─
//
// data is null when the read failed - the form then shows the campaign's own
// send type read-only rather than a wrong set of options.

export const fetchEmailChannels = async (
  accessToken: string | null = null
): Promise<{ data: EmailChannels | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.CHANNELS_GET), {
      method: API_ROUTES.EMAIL.CHANNELS_GET.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to load the send types: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: unknown }
    if (!json.data) throw new Error('The send types came back empty')
    return { data: validateEmailChannels(json.data) }
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

export const addCampaignCopy = async (
  campaignId: string,
  payload: { kind: EmailCampaignCopyKind; contact_id?: string; email_address?: string },
  accessToken: string | null = null
): Promise<{ data: { id: string; kind: string } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.COPY_ADD, { campaignId }),
      {
        method: API_ROUTES.EMAIL.COPY_ADD.method,
        headers,
        body: JSON.stringify({ data: payload })
      }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to add Cc/Bcc: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: { id: string; kind: string } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const removeCampaignCopy = async (
  copyId: string,
  accessToken: string | null = null
): Promise<{ data: { id: string } | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.EMAIL.COPY_REMOVE, { copyId }),
      { method: API_ROUTES.EMAIL.COPY_REMOVE.method, headers }
    )

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to remove Cc/Bcc: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: { id: string } | null }
    return { data: json.data ?? null }
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

// Global contact typeahead for the Cc/Bcc picker. Reuses GET /contacts/search
// (returns {value, label, email, ...}); the add RPC resolves the address from
// the chosen contact_id, so this only needs id + display name + email.
export interface CopyContactOption {
  value: string
  label: string
  email: string
  first_name?: string
  last_name?: string
}

export const searchContactsForCopy = async (
  searchTerm: string,
  accessToken: string | null = null,
  limit = 20
): Promise<{ data: CopyContactOption[]; error?: string }> => {
  try {
    const term = (searchTerm || '').trim()
    if (!term) return { data: [] }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const base = buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.SEARCH)
    const url = `${base}?search=${encodeURIComponent(term)}&limit=${limit}`
    const response = await fetch(url, { method: API_ROUTES.CONTACTS.SEARCH.method, headers })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const body = await readJsonBody<{ message?: string }>(response)
      throw new Error(body?.message || `Failed to search contacts: ${response.status} ${response.statusText}`)
    }
    const json = (await response.json()) as { data?: CopyContactOption[] }
    return { data: Array.isArray(json.data) ? json.data : [] }
  } catch (error) {
    return { data: [], error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
